import type { ReactElement } from 'react'

export type SearchCategory =
  | 'charger'
  | 'site'
  | 'vehicle'
  | 'user'
  | 'org'
  | 'synoplink'
  | 'solarArray'
  | 'page'

export type ActionKind = 'view' | 'navigate' | 'cloak' | 'action'

/**
 * An app-level command surfaced in Cmd+K that runs a callback instead of navigating
 * (e.g. "Enter Demo Mode", "Feature Flags"). Rendered under the Pages group.
 * Availability is decided by the caller, which omits commands the user can't run.
 */
export type QuickSearchCommand = {
  id: string
  label: string
  icon: ReactElement
  onSelect: () => void
}

type MatchableField = 'chargerId' | 'evseId' | 'vin' | 'synoplinkId' | 'serialNumber'

/** Describes which field matched the search query when it's not the primary label */
export type MatchedOn = {
  /** Translation key for the field name (e.g., 'chargerId', 'vin') */
  field: MatchableField
  /** The actual value that matched */
  value: string
}

export type SearchResult = {
  id: string
  category: SearchCategory
  label: string
  subLabel?: string
  /** Full text for tooltip (used when subLabel is truncated) */
  fullLabel?: string
  action: ActionKind
  icon?: ReactElement
  /** When the match was made on a non-displayed field (e.g., charger ID, VIN) */
  matchedOn?: MatchedOn
  onSelect: () => void
}

export type ViewAllItem = {
  type: 'viewAll'
  category: SearchCategory
  hiddenCount: number
  onSelect: () => void
}

/** Union type for items in the flat navigation list */
export type FlatItem = SearchResult | ViewAllItem

export type CategorizedResults = {
  [K in SearchCategory]: SearchResult[]
}

export type CategoryTotals = {
  [K in SearchCategory]: number
}

/** A group of results for a single category, ready for GroupedVirtuoso */
export type ResultGroup = {
  category: SearchCategory
  items: FlatItem[]
  isLoading: boolean
}

export type QuickSearchResults = {
  /** Results grouped by category (only categories with results) */
  groups: ResultGroup[]
  /** Total result count per category (before slicing) */
  totals: CategoryTotals
  /** Categories that are effectively expanded (includes auto-expanded single category) */
  effectiveExpandedCategories: Set<SearchCategory>
  /** Whether only one category has results */
  isSingleCategory: boolean
  /** Active category filter from a prefix like "charger:" or "c:", or null if none */
  filterCategory: SearchCategory | null
  /** The parsed search term after removing any filter prefix */
  searchTerm: string
}

export type QuickSearchPage = {
  id: string
  label: string
  href: string
  subLabel?: string
  icon?: ReactElement
  /**
   * Localized search aliases. Matched the same way as `label`/`subLabel` but
   * never rendered — this is just extra fodder for the substring matcher so
   * users can find a page by colloquial / alternate terms.
   */
  keywords?: string[]
}

/**
 * Represents a recently visited page or entity.
 * Stored in localStorage and converted to SearchResult for display.
 */
export type RecentActivityItem = {
  id: string
  path: string
  category: SearchCategory
  label: string
  subLabel?: string
  timestamp: number
}

export const MAX_RESULTS_PER_CATEGORY = 4
export const MAX_RECENT_ITEMS = 10

// Order in which categories should be displayed
export const CATEGORY_ORDER: SearchCategory[] = [
  'charger',
  'site',
  'vehicle',
  'user',
  'org',
  'synoplink',
  'solarArray',
  'page'
]

/** Type guard to check if a flat item is a ViewAllItem */
export function isViewAllItem(item: FlatItem): item is ViewAllItem {
  return 'type' in item && item.type === 'viewAll'
}
